import {type FC, useCallback, useState} from 'react';
import {JOURNEY_EVENT} from '../../../enums/tagging/index.js';
import {runAnalytics} from '../../analytics.js';

type TaggingFieldDropDownHandlerProps<P> = {
  taggingLabel: string;
  value: any;
  onBlur?: (...args: any[]) => void;
  onFocus?: (...args: any[]) => void;
  error?: string;
} & P;

/*
  Higher order component that wraps components such as TextFields and Dropdowns, and triggers the analytics function onBlur.
  Tracks dwell time and proxies any onBlur or onFocus functions that are passed to the component as a prop.
  Can capture values and send them to adobe analytics if captureInput={true} is passed as a prop.
*/

const withTaggingFieldDropDownClickHandler = <P,>(Component: FC<P>) => {
  const FieldWithTagging: FC<TaggingFieldDropDownHandlerProps<P>> = ({
    taggingLabel,
    value,
    onBlur,
    onFocus,
    error,
    ...rest
  }: TaggingFieldDropDownHandlerProps<P>) => {
    const [startTime, setStartTime] = useState<number>(0);

    const [dropDownValue, setDropDownValue] = useState('');

    const handleOnBlur = useCallback(
      (...args: any) => {
        if (onBlur) {
          onBlur(...args);
        }

        setDropDownValue(value);
        const eventNarrative = `${taggingLabel}${
          dropDownValue ? `:${dropDownValue}` : ''
        }`;

        if (taggingLabel) {
          runAnalytics((LBGAnalytics) => {
            LBGAnalytics.events.send({
              JourneyEvent: JOURNEY_EVENT.FORM_ENTRY,
              EventAction: JOURNEY_EVENT.FIELD_UPDATE,
              EventNarrative: eventNarrative,
              QAAnswer: dropDownValue,
              QAQuestion: taggingLabel,
              dwell_in_ms: Date.now() - startTime,
              LinkValue: undefined,
              ErrorMessage: error,
              JourneyActionNarrative: error,
            });
          });
        }
      },
      [onBlur, taggingLabel, startTime, error, value, dropDownValue]
    );

    const handleOnFocus = useCallback(
      (...args: any) => {
        if (onFocus) {
          onFocus(...args);
        }

        setStartTime(Date.now());
      },
      [setStartTime, onFocus]
    );

    return (
      <Component
        {...(rest as P)}
        error={error}
        onBlur={handleOnBlur}
        onFocus={handleOnFocus}
      />
    );
  };

  return FieldWithTagging;
};

export default withTaggingFieldDropDownClickHandler;
